import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Close } from '@mui/icons-material';
import { Plus } from 'react-feather';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';

// Components
import BarChart from 'src/components/HighChart/Barchart';

// Utilities
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { useQueryDataContainersTerminal } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { IContainersTerminal } from 'src/utils/typings/response';
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import IconFeather from 'src/components/common/Icon';
import Table from 'src/components/Table';
import { slice } from 'lodash';

interface IProps {
  locode: string;
}

const ContainersTerminalChart = ({ locode = '' }: IProps) => {
  const theme = useTheme();
  const [showTable, setShowTable] = useState(false);
  const { data, isFetching } = useQueryDataContainersTerminal({ locode });
  const dataContainersTerminalFull = getArrayDefault(data?.data);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT_GET_LIST);
  const [listContainersTerminal, setListContainersTerminal] = useState<Array<IContainersTerminal>>([]);

  useEffect(() => {
    const initialList = slice(getArrayDefault(dataContainersTerminalFull), page * pageSize, (page + 1) * pageSize);
    setListContainersTerminal(initialList);
  }, [dataContainersTerminalFull, page]);

  const lineChartYData = useMemo(() => {
    const importData: Array<number> = [];
    const exportData: Array<number> = [];
    const transshipmentData: Array<number> = [];
    dataContainersTerminalFull.map((item: IContainersTerminal) => {
      importData.push(item.imports);
      exportData.push(item.exports);
      transshipmentData.push(item.transshipments);
    });
    return { importData: importData, exportData: exportData, transshipmentData: transshipmentData };
  }, [dataContainersTerminalFull]);

  const lineChartXData = useMemo(() => {
    const res = dataContainersTerminalFull.map((item: IContainersTerminal) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [dataContainersTerminalFull]);

  const configColumn: ConfigCell<IContainersTerminal>[] = [
    {
      id: 'date',
      disablePadding: true,
      disableSort: true,
      label: 'Date',
      cell: (row: IContainersTerminal) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.date)}
          </Typography>
        );
      },
    },
    {
      id: 'transshipments',
      disablePadding: true,
      disableSort: true,
      label: 'Transshipments',
      cell: (row: IContainersTerminal) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.transshipments)}
          </Typography>
        );
      },
    },
    {
      id: 'imports',
      disablePadding: true,
      disableSort: true,
      label: 'Imports',
      cell: (row: IContainersTerminal) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.imports)}
          </Typography>
        );
      },
    },
    {
      id: 'exports',
      disablePadding: true,
      disableSort: true,
      label: 'Exports',
      cell: (row: IContainersTerminal) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.exports)}
          </Typography>
        );
      },
    },
  ];

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" align="left">
          Full containers dwelling in port (in thousand TEU)
        </Typography>
        <IconButton onClick={() => setShowTable(!showTable)} color="primary">
          {showTable ? <Close /> : <IconFeather icon={<Plus />} />}
        </IconButton>
      </Stack>
      {showTable ? (
        <Table
          data={listContainersTerminal}
          configColumn={configColumn}
          dataKey="date"
          onChangePage={(e) => setPage(e)}
          onChangePageSize={(e) => setPageSize(e)}
          page={page}
          pageSize={pageSize}
          maxRow={10}
          totalRecords={dataContainersTerminalFull.length}
          sxTableContainer={{
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : (
        <BarChart
          loading={isFetching}
          tooltipFormatter={function () {
            return `<b style="color: ${theme.palette.text.secondary}"></b><b>${this.point.category}</b>
<br/><b style="color: ${theme.palette.text.secondary}">Sea Shipments </b><b>${this.y}</b>`;
          }}
          yAxis={{
            min: 0,
            stackLabels: {
              enabled: false,
            },
          }}
          xAxis={{
            allowDecimals: false,
            labels: {
              formatter: function () {
                const month = String(this.value).split('/')[1]; // Extract month from categories
                const year = String(this.value).split('/')[2]; // Extract year from categories
                return `${month}/${year}`;
              },
            },
            tickInterval: 4, // Show one tick per month
            categories: lineChartXData,
          }}
          legend={{
            align: 'center',
            verticalAlign: 'bottom',
            backgroundColor: 'white',
            borderColor: 'transparent',
            borderWidth: 1,
            shadow: false,
          }}
          plotOptions={{
            column: {
              stacking: 'normal',
            },
          }}
          series={[
            {
              name: 'Exports',
              type: 'column',
              data: lineChartYData.exportData,
              color: '#4338CA',
              borderWidth: 0,
            },
            {
              name: 'Imports',
              type: 'column',
              data: lineChartYData.importData,
              color: '#6366F1',
              borderWidth: 0,
            },
            {
              name: 'Transshipments',
              type: 'column',
              data: lineChartYData.transshipmentData,
              color: '#A5B4FC',
              borderWidth: 0,
            },
          ]}
          height={500}
        />
      )}
    </StyledWhiteBox>
  );
};

export default ContainersTerminalChart;
